import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { toast } from 'react-toastify';
import { apiServices } from '../utils/apiServices';
import TraderDetailOverview from '../components/trader/TraderDetailOverview';
import TraderStakesHistory from '../components/trader/TraderStakesHistory';
import TraderAddWithdrawCollateralModal from '../components/trader/TraderAddWithdrawCollateralModal';
import TraderStakingForm from '../components/trader/TraderStakingForm';
import { IStakeResponse, ITraderResponse } from '../utils/types';
import { PlusIcon, ArrowUpIcon, ChevronLeftIcon, RefreshIcon } from '../components/SVGIcons';
import { useTheme } from '../context/ThemeContext';
import UnstakeConfirmModal from '../components/trader/UnstakeConfirmModal';
import { useEVMContext } from '../context/EVMContext';
import { ethers } from 'ethers';
import { parseContractEvents } from '../utils';

const TraderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { api, setIsLoading } = useAppContext();
  const navigate = useNavigate();
  const { signer, accounts, provider, poolContract } = useEVMContext();
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUnstakeConfirmModalOpen, setIsUnstakeConfirmModalOpen] = useState(false);
  const [activeStake, setActiveStake] = useState<IStakeResponse | null>(null);
  const [actionType, setActionType] = useState<'add' | 'withdraw'>('add');
  const [amount, setAmount] = useState('');
  const [traderData, setTraderData] = useState<ITraderResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useTheme();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [balance, setBalance] = useState<string>('');

  const updateBalance = useCallback(async() => {
    if (!(window as any).ethereum) {
      throw new Error('No EVM wallet detected');
    }
    const connectedWalletAddress = await signer?.getAddress();
    if (connectedWalletAddress && provider) {
      const balance = await provider.getBalance(connectedWalletAddress);
      console.log('EVM Balance:', ethers.formatEther(balance));
      // Convert balance from wei to ether
      setBalance(ethers.formatEther(balance));
    }
  }, [signer]);

  useEffect(() => {
    updateBalance();
  }, [updateBalance, signer]);

  const getTraderData = async () => {
    if (!id) return;
    // setIsLoading(true);
    setIsRefreshing(true);
    setError(null);
    try {
      const res = await apiServices.traders.getById(id);
      setTraderData(res);
    } catch (err) {
      console.error('Failed to fetch trader details:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch trader details');
      setTraderData(null);
    } finally {
      // setIsLoading(false);
      setIsRefreshing(false);
    }
  }

  const refreshTraderData = useCallback(() => {
    if (id) {
      getTraderData();
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    getTraderData();
    // const interval = setInterval(() => {
    //   if (id) {
    //     getTraderData();
    //   }
    // }, 60000); // 1 minute in milliseconds

    // return () => clearInterval(interval); // Cleanup on unmount
  }, [id]);

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const handleAddCollateral = async () => {
    setActionType('add');
    setIsModalOpen(true);
  };

  const handleWithdraw = async () => {
    setActionType('withdraw');
    setIsModalOpen(true);
  };

  const handleUnstakeAllSubmit = () => {
    if (activeStake) {
      handleUnstakeAll(activeStake)
    }
  }

  const handleUnstakeAll = async (stake: IStakeResponse) => {
    if (!id) {
      toast.error('Trader ID is not defined');
      return;
    }
    if (!poolContract || !signer) {
      toast.error('EVM Wallet is not connected');
      return;
    }
    setIsLoading(true);
    const connectedWalletAddress = await signer?.getAddress();

    try {
      poolContract.unstakeAllFromSubnet(
        stake.subnet,
      ).then((tx: any) => {
        return tx.wait();
      }).then(async (receipt: any) => {
        // Parse event from logs
        parseContractEvents(receipt, poolContract);
        toast.success('Unstake successful!');

        await apiServices.pool.refresh(connectedWalletAddress, false);
        getTraderData();
      }).catch((error: any) => {
        console.error('Error while deposits:', error);
        toast.error(`Failed to deposit: ${error.message || 'Please try again.'}`);
      });
    } catch (error) {
      console.log(`Failed to stake:`, error);
      toast.error(`Failed to stake. Please try again.`);
    }
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    if (!id) {
      toast.error('Trader ID is not defined');
      return;
    }
    try {
      if (!poolContract || !signer) {
        toast.error('EVM Wallet is not connected');
        return;
      }
      setIsLoading(true);
      const connectedWalletAddress = await signer?.getAddress();
      if (actionType === 'add') {
        poolContract.addCollateral({
          value: ethers.parseEther(amount)
        }).then((tx: any) => {
          return tx.wait();
        }).then(async (receipt: any) => {
          // Parse event from logs
          parseContractEvents(receipt, poolContract);
          toast.success('Collateral added successfully!');

          const res = await apiServices.pool.refresh(connectedWalletAddress, true);
          setTraderData(res as ITraderResponse);
          updateBalance();
        }).catch((error: any) => {
          console.error('Error adding collateral:', error);
          toast.error(`Failed to add collateral: ${error.message || 'Please try again.'}`);
        });
      } else {
        poolContract.removeCollateral(ethers.parseEther(amount)).then((tx: any) => {
          return tx.wait();
        }).then(async (receipt: any) => {
          // Parse event from logs
          parseContractEvents(receipt, poolContract);
          toast.success('Collateral withdrawn successfully!');
          const res = await apiServices.pool.refresh(connectedWalletAddress, true);
          setTraderData(res as ITraderResponse);

          updateBalance();
        }).catch((error: any) => {
          console.error('Error withdrawing collateral:', error);
          toast.error(`Failed to withdraw collateral: ${error.message || 'Please try again.'}`);
        });
      }
    } catch (error) {
      console.log(`Failed to ${actionType} collateral:`, error);
      toast.error(`Failed to ${actionType} collateral: Please try again.`);
    } finally {
      setIsLoading(false);
      setIsModalOpen(false);
      setAmount('');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setAmount('');
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="rounded-[100px] p-2 bg-gray-200 dark:bg-gray-800 mr-4"
            >
              <ChevronLeftIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
            </button>
            <h1 className="text-2xl sm:text-3xl font-borna text-black dark:text-white">Trader Details</h1>
          </div>
          {error && (
            <div className="text-red-600 text-sm">
              {error}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            {accounts[0] === id?.toLowerCase() && (
              <>
                <button
                  onClick={handleAddCollateral}
                  className="py-2 bg-green-500 text-white dark:text-black rounded-[100px] hover:bg-green-700 transition-colors text-[14px] h-[40px] sm:h-[46px] px-[16px] sm:px-[24px] flex items-center justify-center"
                >
                  <PlusIcon fillColor={theme === 'dark' ? 'black' : 'white'} />
                  <span className="ml-2">Add Collateral</span>
                </button>
                <button
                  onClick={handleWithdraw}
                  className="py-2 bg-gray-100 hover:bg-gray-300 dark:bg-gray-800 text-black dark:text-white rounded-[100px] dark:hover:bg-gray-500 transition-colors text-[14px] h-[40px] sm:h-[46px] px-[16px] sm:px-[24px] flex items-center justify-center"
                >
                  <ArrowUpIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
                  <span className="ml-2">Withdraw</span>
                </button>
              </>
            )}
            <button
              onClick={refreshTraderData}
              className="p-2 bg-[#F2F2F2] hover:opacity-80 dark:bg-[#262626] text-black dark:text-white rounded-[100px] transition-opacity text-[14px] w-[40px] sm:w-[46px] h-[40px] sm:h-[46px] flex items-center justify-center"
            >
              <span className={`${isRefreshing ? 'animate-spin' : ''}`}>
                <RefreshIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          <TraderDetailOverview traderData={traderData} handleCopyAddress={handleCopyAddress} copiedAddress={copiedAddress || ''} id={id || ''} balance={balance} />

          {/* Staking Form */}
          {accounts[0] === id?.toLowerCase() && (
            <TraderStakingForm refreshFunc={getTraderData} />
          )}

          {traderData && <TraderStakesHistory traderData={traderData} setIsUnstakeConfirmModalOpen={setIsUnstakeConfirmModalOpen} setActiveStake={setActiveStake} traderAddress={id} />}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <TraderAddWithdrawCollateralModal actionType={actionType} handleCloseModal={handleCloseModal} handleSubmit={handleSubmit} amount={amount} setAmount={setAmount} />
      )}

      {isUnstakeConfirmModalOpen && (
        <UnstakeConfirmModal handleSubmit={handleUnstakeAllSubmit} handleCloseModal={() => setIsUnstakeConfirmModalOpen(false)} />
      )}
    </div>
  );
};

export default TraderDetailPage; 